import { BROWSE_PLATFORMS, emptyBrowseFilter, getBrowseFilterFromList, syncBrowseFilterPlatforms } from "../../utils/browseFilter";
import { convertNestedEmptyToNull } from "../../services/firebase";
import { asPlatforms, listPlatforms } from "../../shared/platforms";
import { BrowseFilter, List, Platform, SelectedTalent, Talent } from "../../shared/types";
import { orderedTalentIds } from "../../utils/orderedTalentIds";

export type ListState = {
    creatorRequest?: boolean,
    creatorRequestLockFilters?: boolean,
    filter: BrowseFilter,
    hidePublicFilters?: boolean,
    hidePublicPrices?: boolean,
    listUid?: string,
    name: string,
    notesBookerViewable?: boolean,
    notesRequired?: boolean,
    originalList?: List,
    selectedTalent: SelectedTalent,
    talentOrder: string[],
    orderDirty?: boolean, // used to prevent server updates from messing up in-progress ordering
    useCustomPrices?: boolean,
    platforms: Platform[],
}

export type ListAction =
    { type: "setStatePartial", payload: Partial<ListState> } |
    { type: "setListFromDatabase", list: List } |
    { type: "addTalent", id: string, selectedTalentItem: SelectedTalent["id"], addedBy: string } |
    { type: "updateTalentNotes", id: string, notes: string } |
    { type: "removeTalent", id: string } |
    { type: "reorderTalent", activeId: string, overId: string } |
    { type: "updateFilter", filterPartial: Partial<BrowseFilter> };

export function getInitialState(): ListState {
    return {
        name: "",
        selectedTalent: {},
        talentOrder: [],
        orderDirty: false,
        creatorRequest: false,
        useCustomPrices: false,
        filter: { ...emptyBrowseFilter, showCampaignTypeSelect: true },
        platforms: [],
    };
}

export function getPlatformsFromList(list: List): Platform[] {
    const fromList = listPlatforms(list);
    const fromFilter = asPlatforms(list.filter?.campaignPlatform);
    return fromList.length ? fromList : fromFilter.length ? fromFilter : ["TikTok"];
}

/** One-element lists for TalentBrowse. Membership is stuffed so the roster stays visible; selectedTalent is the actual list. */
export function buildBrowseLists(list: List, rawTalent: Talent[], platforms: Platform[]): List[] {
    const talent: SelectedTalent = { ...(list.talent || {}) };
    for (const tal of rawTalent) {
        if (!talent[tal.id]) {
            talent[tal.id] = {};
        }
    }
    return [{ ...list, active: true, talent, platform: platforms, notesBookerViewable: false }];
}

/** Nested platform keys are always present so Firestore merge overwrites stale ranges instead of keeping them. */
export function getListFilterFromBrowseFilter(filter: BrowseFilter, platforms: Platform[]): BrowseFilter {
    const next = { ...syncBrowseFilterPlatforms(filter, platforms), search: "", listId: "" };
    const selected = new Set(platforms);
    for (const platform of BROWSE_PLATFORMS) {
        const stored = selected.has(platform) ? next[platform] : undefined;
        next[platform] = stored
            ? {
                followersRange: stored.followersRange ?? null,
                priceRange: stored.priceRange ?? null,
            }
            : null;
    }
    return next;
}

export function buildListUpdates(args: {
    listId: string,
    creatorRequest: boolean,
    creatorRequestLockFilters: boolean,
    name: string,
    platforms: Platform[],
    useCustomPrices: boolean,
    filter: BrowseFilter,
    selectedTalent: SelectedTalent,
    talentOrder: string[],
    notesBookerViewable: boolean,
    notesRequired: boolean,
    hidePublicFilters: boolean,
    hidePublicPrices: boolean,
}) {
    return {
        id: args.listId,
        ...convertNestedEmptyToNull({
            creatorRequest: !!args.creatorRequest,
            creatorRequestLockFilters: !!args.creatorRequestLockFilters,
            name: args.name,
            platform: args.platforms,
            ...(args.useCustomPrices
                ? {
                    campaignType: args.filter.campaignType ?? null,
                    currency: args.filter.currency ?? null,
                }
                : {
                    currency: null,
                    campaignType: null,
                }),
            talent: args.selectedTalent,
            talentOrder: args.talentOrder,
            useCustomPrices: args.useCustomPrices,
            filter: getListFilterFromBrowseFilter(args.filter, args.platforms),
            notesBookerViewable: !!args.notesBookerViewable,
            notesRequired: !!args.notesRequired,
            hidePublicFilters: !!args.hidePublicFilters,
            hidePublicPrices: !!args.hidePublicPrices,
        }),
    };
}

export function findDeletedTalentIds(original: SelectedTalent | undefined, selected: SelectedTalent): string[] {
    return Object.keys(original || {}).filter((talId) => !Object.keys(selected).includes(talId));
}

export function findAddedTalentIds(original: SelectedTalent | undefined, selected: SelectedTalent): string[] {
    const originalIds = Object.keys(original || {});
    return Object.keys(selected).filter((id) => !originalIds.includes(id));
}

export function sumCustomTalentPrices(selectedTalent: SelectedTalent) {
    let total = 0;
    for (const tal of Object.values(selectedTalent)) {
        total += tal.price || 0;
    }
    return total;
}

export function sumDefaultTalentPrices(talent: (Talent | undefined)[]) {
    let total = 0;
    for (const tal of talent) {
        if (tal) {
            total += tal.defaultPrice || 0;
        }
    }
    return total;
}

function isTalentRowDirty(original: SelectedTalent[string] | undefined, local: SelectedTalent[string]): boolean {
    return (original?.price ?? null) !== (local.price ?? null)
        || (original?.notes || "") !== (local.notes || "");
}

/** 3-way merge so a live snapshot keeps A's unsaved roster edits and folds in B's saved ones. */
export function mergeListTalent(original: SelectedTalent, local: SelectedTalent, server: SelectedTalent): SelectedTalent {
    const next: SelectedTalent = {};
    const ids = new Set([
        ...Object.keys(original),
        ...Object.keys(local),
        ...Object.keys(server),
    ]);

    for (const id of ids) {
        const inOriginal = Object.hasOwn(original, id);
        const inLocal = Object.hasOwn(local, id);
        const inServer = Object.hasOwn(server, id);

        if (inLocal && !inOriginal) {
            // A's unsaved add: keep
            next[id] = local[id];
            continue;
        }
        if (inServer && !inOriginal) {
            // B's saved add (or first snapshot): take
            next[id] = server[id];
            continue;
        }
        if (inOriginal && !inLocal) {
            // A's unsaved remove: keep removed
            continue;
        }
        if (inOriginal && !inServer) {
            // B deleted: drop, unless A edited the row
            if (inLocal && isTalentRowDirty(original[id], local[id])) {
                next[id] = local[id];
            }
            continue;
        }
        // Both still have it: keep local edit, else take server
        next[id] = isTalentRowDirty(original[id], local[id])
            ? local[id]
            : server[id];
    }

    return next;
}

export function reduceListState(state: ListState, action: ListAction): ListState {
    switch (action.type) {
        case "setStatePartial": {
            return { ...state, ...action.payload } as ListState;
        }
        case "updateFilter": {
            console.log("newFilter", {
                ...state.filter,
                ...action.filterPartial,
            });
            return {
                ...state,
                filter: {
                    ...state.filter,
                    ...action.filterPartial,
                } as BrowseFilter,
            };
        }
        case "setListFromDatabase": {
            const list = action.list;
            const isListSeededFromServer = !!state.originalList;
            const selectedTalent = mergeListTalent(
                state.originalList?.talent || {},
                state.selectedTalent,
                list.talent || {},
            );
            const talentOrder = orderedTalentIds(
                selectedTalent,
                state.orderDirty ? state.talentOrder : list.talentOrder,
            );
            const useCustomPrices = !!(Object.values(selectedTalent).find((tal) => tal.price) || list.useCustomPrices);
            const nextPlatforms = getPlatformsFromList(list);
            const customPriceLocks = useCustomPrices && {
                lockCampaignType: true,
                lockCurrencyFromBooker: true,
                campaignType: (isListSeededFromServer ? state.filter.campaignType : list.campaignType) || "Song",
            };

            return {
                ...state,
                name: isListSeededFromServer ? state.name : list.name,
                creatorRequest: list.creatorRequest,
                creatorRequestLockFilters: list.creatorRequestLockFilters,
                selectedTalent,
                talentOrder,
                orderDirty: state.orderDirty,
                useCustomPrices,
                platforms: isListSeededFromServer ? state.platforms : nextPlatforms,
                filter: isListSeededFromServer
                    ? { ...state.filter, ...customPriceLocks }
                    : {
                        ...getBrowseFilterFromList(list, nextPlatforms),
                        showCampaignTypeSelect: true,
                        ...customPriceLocks,
                    },
                listUid: list.uid,
                originalList: list,
                notesBookerViewable: isListSeededFromServer ? state.notesBookerViewable : list.notesBookerViewable,
                notesRequired: isListSeededFromServer ? state.notesRequired : list.notesRequired,
                hidePublicFilters: isListSeededFromServer ? state.hidePublicFilters : list.hidePublicFilters,
                hidePublicPrices: isListSeededFromServer ? state.hidePublicPrices : list.hidePublicPrices,
            };
        }
        case "addTalent": {
            const { id, selectedTalentItem, addedBy } = action;

            const newSelectedTalent: SelectedTalent = JSON.parse(JSON.stringify(state.selectedTalent));
            const newTalentObj = Object.assign({}, state.selectedTalent[id], { price: state.useCustomPrices ? selectedTalentItem.price : null, addedBy, notes: selectedTalentItem.notes || "" });
            newSelectedTalent[id] = newTalentObj;

            const talentOrder = state.talentOrder.includes(id)
                ? state.talentOrder
                : [...state.talentOrder, id];

            return { ...state, selectedTalent: newSelectedTalent, talentOrder };
        }
        case "updateTalentNotes": {
            const { id, notes } = action;
            const newSelectedTalent: SelectedTalent = JSON.parse(JSON.stringify(state.selectedTalent));
            newSelectedTalent[id].notes = notes;
            return { ...state, selectedTalent: newSelectedTalent };
        }
        case "removeTalent": {
            const { id } = action;
            const newSelectedTalent: SelectedTalent = JSON.parse(JSON.stringify(state.selectedTalent));
            delete newSelectedTalent[id];
            const talentOrder = state.talentOrder.filter((talentId) => talentId !== id);
            return { ...state, selectedTalent: newSelectedTalent, talentOrder };
        }
        case "reorderTalent": {
            const { activeId, overId } = action;
            const oldIndex = state.talentOrder.indexOf(activeId);
            const newIndex = state.talentOrder.indexOf(overId);
            if (oldIndex === -1 || newIndex === -1) {
                return state;
            }
            const talentOrder = state.talentOrder.slice();
            const [moved] = talentOrder.splice(oldIndex, 1);
            talentOrder.splice(newIndex, 0, moved);
            return {
                ...state,
                talentOrder,
                orderDirty: true,
            };
        }
    }
}
